/**
 * BaseController - A Stimulus-inspired Web Component base class
 * Provides targets, values, actions, and lifecycle management for Shopify themes
 */

class BaseController extends HTMLElement {
  static instances = new Map(); // Global registry for cross-component targeting
  static idCounters = new Map(); // Auto-ID generation counters
  static globalActionListener = null; // Global event delegation for cross-component actions
  static breakpoints = {
    sm: "(min-width: 640px)",
    md: "(min-width: 768px)",
    lp: "(min-width: 1024px) and (max-width: 1440px)",
    lg: "(min-width: 1024px)",
    xl: "(min-width: 1280px)",
  };

  constructor() {
    super();

    // Initialize instance properties
    this.targets = new Map();
    this.values = new Map();
    this.responsiveValues = new Map(); // Store responsive value configs
    this.valuesByBreakpoint = {};
    this.mediaQueries = new Map(); // Store matchMedia objects
    this.actionBindings = new Set();
    this.observer = null;
    this.controllerId = null;
    this.controllerName = this.constructor.controllerName;

    // Setup global action listener if not already done
    if (!BaseController.globalActionListener) {
      BaseController.setupGlobalActionListener();
    }
  }

  // Auto-register component when class is defined
 static register() {
  if (!this.controllerName) {
    this.controllerName = this.name
      .replace(/Controller$/, "")
      .replace(/([A-Z])/g, "-$1")
      .toLowerCase()
      .replace(/^-/, "");
  }

  // Skip if already registered
  if (customElements.get(this.controllerName)) {
    return;
  }

  console.log(
    `Registering component: ${this.name} as <${this.controllerName}>`
  );

  // Register autonomous custom element - just use customElements.define directly
  try {
    customElements.define(this.controllerName, this);
  } catch (e) {
    if (!e.message.includes("already been defined")) {
      console.warn(`Failed to register ${this.controllerName}:`, e);
    }
  }

  // Try to register customized built-in element (Safari doesn't support this)
  try {
    customElements.define(
      this.controllerName + "-builtin",
      this,
      { extends: "div" }
    );
  } catch (e) {
    // Safari will throw here - that's expected and fine
    if (
      !e.message.includes("already been defined") &&
      !e.message.includes("NotSupportedError") &&
      !e.message.includes("not supported")
    ) {
      console.warn(
        `Customized built-in elements not supported in this browser`
      );
    }
  }
}

  connectedCallback() {
    this.setupController();
    this.bindActions();
    this.findTargets();
    this.setupValues();
    this.setupResponsiveValues();
    this.startObserving();

    // Call user-defined initialize method
    if (typeof this.initialize === "function") {
      this.initialize();
    }
  }

  disconnectedCallback() {
    this.stopObserving();
    this.unbindActions();
    this.cleanupResponsiveValues();
    this.removeFromGlobalRegistry();

    // Call user-defined disconnect method
    if (typeof this.disconnect === "function") {
      this.disconnect();
    }
  }

  setupController() {
    // Generate or get controller ID - support both naming conventions
    let providedId = this.getAttribute(`data-controller`);

    if (!providedId) {
      // Fall back to old naming
      providedId = this.getAttribute(`data-${this.controllerName}-controller`);
    }

    if (providedId) {
      this.controllerId = providedId;
    } else {
      // Auto-generate ID
      const counter = BaseController.idCounters.get(this.controllerName) || 0;
      BaseController.idCounters.set(this.controllerName, counter + 1);
      this.controllerId = `${this.controllerName}-${counter + 1}`;
      // Use clean naming for new IDs
      this.setAttribute(`data-controller`, this.controllerId);
    }

    // Register in global instances map
    BaseController.instances.set(this.controllerId, this);
  }

  removeFromGlobalRegistry() {
    if (this.controllerId) {
      BaseController.instances.delete(this.controllerId);
    }
  }

  // Target management
  findTargets() {
    const targetNames = this.constructor.targets || [];

    targetNames.forEach((targetName) => {
      this.refreshTarget(targetName);
    });
  }

  refreshTarget(targetName) {
    const kebabTargetName = this.camelToKebab(targetName);

    // Support clean naming: data-target="button"
    const cleanTargetSelector = `[data-target~="${kebabTargetName}"]`;
    // Support old naming: data-product-form-target="button"
    const prefixedTargetSelector = `[data-${this.controllerName}-target~="${kebabTargetName}"]`;
    // Cross-component targeting
    const crossTargetSelector = `[data-target~="${kebabTargetName}"][data-controller="${this.controllerId}"], [data-${this.controllerName}-target~="${kebabTargetName}"][data-${this.controllerName}-controller="${this.controllerId}"]`;

    // Find targets within this component
    const localTargets = [
      ...Array.from(this.querySelectorAll(cleanTargetSelector)),
      ...Array.from(this.querySelectorAll(prefixedTargetSelector)),
    ];

    // Find cross-component targets
    const crossTargets = Array.from(
      document.querySelectorAll(crossTargetSelector)
    );

    // console.log('CROSS TARGETS:', crossTargetSelector);

    const allTargets = [...localTargets, ...crossTargets];

    // Create target accessors
    const camelTargetName = this.kebabToCamel(targetName);

    // Single target accessor
    Object.defineProperty(this, `${camelTargetName}Target`, {
      get: () => allTargets[0] || null,
      configurable: true,
    });

    // Multiple targets accessor
    Object.defineProperty(this, `${camelTargetName}Targets`, {
      get: () => allTargets,
      configurable: true,
    });

    // Has target checker
    Object.defineProperty(
      this,
      `has${this.capitalize(camelTargetName)}Target`,
      {
        get: () => allTargets.length > 0,
        configurable: true,
      }
    );

    this.targets.set(targetName, allTargets);
  }

  // Value management
  setupValues() {
    const valueNames = this.constructor.values || [];

    valueNames.forEach((valueName) => {
      this.setupValue(valueName);
    });

    this.setupValuesByBreakpoint();
  }

  setupValuesByBreakpoint() {
    const namedBreakpoints = Object.keys(this.constructor.breakpoints).reduce(
      (acc, key) => {
        acc[key] = {};
        return acc;
      },
      {}
    );

    this.responsiveValues.entries().forEach(([key, value]) => {
      Object.keys(this.constructor.breakpoints).forEach((breakpoint) => {
        if (value[breakpoint]) {
          namedBreakpoints[breakpoint][key] = this.parseValue(
            value[breakpoint]
          );
        }
      });
    });

    this.valuesByBreakpoint = {
      ...namedBreakpoints,
    };
    // this.valuesByBreakpoint.set(this.controllerName, namedBreakpoints);
  }

  setupValue(valueName) {
    // Support both old (prefixed) and new (clean) attribute naming
    const kebabValueName = this.camelToKebab(valueName);
    const prefixedAttrName = `data-${this.controllerName}-${kebabValueName}`;
    const cleanAttrName = `data-${kebabValueName}`;

    const camelValueName = this.kebabToCamel(valueName);

    // Check for clean attribute first, fall back to prefixed
    let currentAttr = cleanAttrName;
    let baseValue = this.getAttribute(cleanAttrName);

    if (baseValue === null) {
      currentAttr = prefixedAttrName;
      baseValue = this.getAttribute(prefixedAttrName);
    }

    // Get responsive config for both naming conventions
    const responsiveConfig = this.getResponsiveConfig(
      valueName,
      kebabValueName
    );

    // Determine current value based on breakpoints
    const currentValue = this.getCurrentResponsiveValue(
      valueName,
      baseValue,
      responsiveConfig
    );

    // Store initial value
    this.values.set(valueName, this.parseValue(currentValue));

    // Store responsive config if it exists
    if (Object.keys(responsiveConfig).length > 0) {
      this.responsiveValues.set(valueName, {
        base: baseValue,
        attr: currentAttr,
        ...responsiveConfig,
      });
    }

    // Create value accessor
    Object.defineProperty(this, `${camelValueName}Value`, {
      get: () => this.values.get(valueName),
      set: (newValue) => {
        const oldValue = this.values.get(valueName);
        const parsedValue = this.parseValue(newValue);

        if (oldValue !== parsedValue) {
          this.values.set(valueName, parsedValue);
          // Update the attribute we're actually using
          this.setAttribute(currentAttr, String(newValue));

          // Call value changed callback if it exists
          const callbackName = `${camelValueName}ValueChanged`;
          if (typeof this[callbackName] === "function") {
            this[callbackName](parsedValue, oldValue);
          }
        }
      },
      configurable: true,
    });

    // Has value checker - check both attribute styles
    Object.defineProperty(this, `has${this.capitalize(camelValueName)}Value`, {
      get: () =>
        this.hasAttribute(cleanAttrName) || this.hasAttribute(prefixedAttrName),
      configurable: true,
    });
  }

  // Responsive value management
  setupResponsiveValues() {
    const valueNames = this.constructor.values || [];

    valueNames.forEach((valueName) => {
      const responsiveConfig = this.responsiveValues.get(valueName);
      if (responsiveConfig) {
        this.setupResponsiveListeners(valueName, responsiveConfig);
      }
    });
  }

  getResponsiveConfig(valueName, kebabValueName) {
    const config = {};

    // Check both clean and prefixed responsive attributes
    Object.keys(BaseController.breakpoints).forEach((breakpoint) => {
      // Try clean naming first: data-product-id:lg
      const cleanResponsiveAttr = `data-${kebabValueName}:${breakpoint}`;
      let value = this.getAttribute(cleanResponsiveAttr);

      // Fall back to prefixed naming: data-product-form-product-id:lg
      if (value === null) {
        const prefixedResponsiveAttr = `data-${this.controllerName}-${kebabValueName}:${breakpoint}`;
        value = this.getAttribute(prefixedResponsiveAttr);
      }

      if (value !== null) {
        config[breakpoint] = value;
      }
    });

    return config;
  }

  getCurrentResponsiveValue(valueName, baseValue, responsiveConfig) {
    // Start with base value
    let currentValue = baseValue;

    // Check each breakpoint in mobile-first order
    const breakpointOrder = ["sm", "md", "lg", "xl"];

    breakpointOrder.forEach((breakpoint) => {
      if (responsiveConfig[breakpoint] !== undefined) {
        const mediaQuery = window.matchMedia(
          BaseController.breakpoints[breakpoint]
        );
        if (mediaQuery.matches) {
          currentValue = responsiveConfig[breakpoint];
        }
      }
    });

    return currentValue;
  }

  setupResponsiveListeners(valueName, responsiveConfig) {
    const camelValueName = this.kebabToCamel(valueName);

    // Create media query listeners for each responsive breakpoint
    Object.keys(responsiveConfig).forEach((breakpoint) => {
      if (breakpoint === "base") return;

      const mediaQuery = window.matchMedia(
        BaseController.breakpoints[breakpoint]
      );
      const handler = () => {
        const newValue = this.getCurrentResponsiveValue(
          valueName,
          responsiveConfig.base,
          responsiveConfig
        );
        const oldValue = this.values.get(valueName);
        const parsedNewValue = this.parseValue(newValue);

        if (parsedNewValue !== oldValue) {
          this.values.set(valueName, parsedNewValue);

          // Call value changed callback if it exists
          const callbackName = `${camelValueName}ValueChanged`;
          if (typeof this[callbackName] === "function") {
            this[callbackName](parsedNewValue, oldValue);
          }
        }
      };

      mediaQuery.addListener(handler);
      this.mediaQueries.set(`${valueName}-${breakpoint}`, {
        mediaQuery,
        handler,
      });
    });
  }

  cleanupResponsiveValues() {
    this.mediaQueries.forEach(({ mediaQuery, handler }) => {
      mediaQuery.removeListener(handler);
    });
    this.mediaQueries.clear();
  }

  // Action management
  bindActions() {
    const selfAction = this.getAttribute("data-action");

    if (selfAction) {
      const actions = selfAction.split(" ");

      actions.forEach((actionString) => {
        this.bindAction(this, actionString);
      });
    }

    const actionElements = this.querySelectorAll("[data-action]");

    actionElements.forEach((element) => {
      const actions = element.getAttribute("data-action").split(" ");

      actions.forEach((actionString) => {
        this.bindAction(element, actionString);
      });
    });
  }

  bindAction(element, actionString) {
    const [eventType, controllerAction] = actionString.split("->");

    if (!controllerAction) return;

    const [controllerName, methodName] = controllerAction.split("#");

    // Check if this action is for this controller
    if (controllerName === this.controllerName) {
      const handler = (event) => {
        if (typeof this[methodName] === "function") {
          this[methodName](event);
        }
      };

      element.addEventListener(eventType, handler);
      this.actionBindings.add({ element, eventType, handler });
    }
  }

  unbindActions() {
    this.actionBindings.forEach(({ element, eventType, handler }) => {
      element.removeEventListener(eventType, handler);
    });
    this.actionBindings.clear();
  }

  // Global action listener for cross-component actions
  static setupGlobalActionListener() {
    BaseController.globalActionListener = (event) => {
      const target = event.target;
      const actionAttr = target.getAttribute("data-action");

      if (!actionAttr) return;

      const actions = actionAttr.split(" ");

      actions.forEach((actionString) => {
        const [eventType, controllerAction] = actionString.split("->");

        if (event.type !== eventType || !controllerAction) return;

        const [controllerName, methodName] = controllerAction.split("#");

        // Find target controller instance
        const controllerSelector = `[data-${controllerName}-controller]`;
        const controllerElement =
          target.closest(controllerSelector) ||
          document.querySelector(controllerSelector);

        if (controllerElement) {
          const controllerId = controllerElement.getAttribute(
            `data-${controllerName}-controller`
          );
          const controllerInstance = BaseController.instances.get(controllerId);

          if (
            controllerInstance &&
            typeof controllerInstance[methodName] === "function"
          ) {
            controllerInstance[methodName](event);
          }
        }
      });
    };

    // Add global event listeners for common events
    ["click", "change", "input", "submit", "focus", "blur"].forEach(
      (eventType) => {
        document.addEventListener(
          eventType,
          BaseController.globalActionListener,
          true
        );
      }
    );
  }

  // Mutation observation for dynamic changes
  startObserving() {
    this.observer = new MutationObserver((mutations) => {
      let shouldRefreshTargets = false;
      let shouldRefreshValues = false;

      mutations.forEach((mutation) => {
        if (mutation.type === "attributes") {
          const attrName = mutation.attributeName;

          // Check for target changes
          if (attrName.includes("target")) {
            shouldRefreshTargets = true;
          }

          // Check for value changes
          if (
            attrName.startsWith(`data-${this.controllerName}-`) &&
            !attrName.includes("target") &&
            !attrName.includes("controller")
          ) {
            shouldRefreshValues = true;

            // Trigger value change callback
            const valueName = attrName.replace(
              `data-${this.controllerName}-`,
              ""
            );
            this.handleValueChange(
              valueName,
              mutation.target.getAttribute(attrName),
              mutation.oldValue
            );
          }
        }

        if (mutation.type === "childList") {
          shouldRefreshTargets = true;
        }
      });

      if (shouldRefreshTargets) {
        this.findTargets();
        this.unbindActions();
        this.bindActions();
      }
    });

    this.observer.observe(this, {
      attributes: true,
      attributeOldValue: true,
      childList: true,
      subtree: true,
    });

    // Also observe document for cross-component targets
    this.observer.observe(document.body, {
      attributes: true,
      attributeOldValue: true,
      childList: true,
      subtree: true,
    });
  }

  stopObserving() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  handleValueChange(valueName, newValue, oldValue) {
    const camelValueName = this.kebabToCamel(valueName);

    // Check if this is a responsive attribute change
    if (newValue !== null && newValue.includes(":")) {
      // This might be a responsive attribute, refresh the responsive setup
      const responsiveConfig = this.getResponsiveConfig(valueName);
      if (Object.keys(responsiveConfig).length > 0) {
        this.responsiveValues.set(valueName, {
          base: this.getAttribute(`data-${this.controllerName}-${valueName}`),
          ...responsiveConfig,
        });
        this.cleanupResponsiveValues();
        this.setupResponsiveListeners(
          valueName,
          this.responsiveValues.get(valueName)
        );
      }
    }

    const parsedNewValue = this.parseValue(newValue);
    const parsedOldValue = this.parseValue(oldValue);

    if (parsedNewValue !== parsedOldValue) {
      this.values.set(valueName, parsedNewValue);

      const callbackName = `${camelValueName}ValueChanged`;
      if (typeof this[callbackName] === "function") {
        this[callbackName](parsedNewValue, parsedOldValue);
      }
    }
  }

  // Utility methods
  parseValue(value) {
    if (value === null || value === undefined) return null;
    if (value === "true") return true;
    if (value === "false") return false;
    if (!isNaN(value) && !isNaN(parseFloat(value))) return parseFloat(value);
    return value;
  }

  kebabToCamel(str) {
    return str.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
  }

  camelToKebab(str) {
    return str.replace(/([A-Z])/g, "-$1").toLowerCase();
  }

  capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  // Public API methods
  refreshTargets() {
    this.findTargets();
  }

  refreshActions() {
    this.unbindActions();
    this.bindActions();
  }

  // Static method to get controller instance by ID
  static getInstance(controllerId) {
    return BaseController.instances.get(controllerId);
  }

  // Static method to get all instances of a controller type
  static getInstances(controllerName) {
    const instances = [];
    BaseController.instances.forEach((instance, id) => {
      if (id.startsWith(controllerName + "-")) {
        instances.push(instance);
      }
    });
    return instances;
  }
}

// Simple auto-registration helper for dynamic imports
BaseController.autoRegister = function (ControllerClass) {
  if (ControllerClass.prototype instanceof BaseController) {
    ControllerClass.register();
  }
  return ControllerClass;
};

export default BaseController;
